// Cliente HTTP del panel de superadministrador — deliberadamente SEPARADO
// de api.ts (mismo patrón, tokens propios, clave de localStorage propia)
// para que una sesión de cliente y una sesión de superadmin puedan convivir
// en el mismo navegador sin pisarse, y para que un token de una nunca sirva
// para la otra (el backend además lo impide con una claim JWT distinta —
// ver requiere_superadmin en app/decorators.py).
import { API_BASE, ApiError } from "./api";

let accessToken: string | null = null;
let refreshToken: string | null = localStorage.getItem("sigepro_superadmin_refresh_token");

export function setSuperadminTokens(access: string | null, refresh: string | null) {
  accessToken = access;
  refreshToken = refresh;
  if (refresh) localStorage.setItem("sigepro_superadmin_refresh_token", refresh);
  else localStorage.removeItem("sigepro_superadmin_refresh_token");
}

export { ApiError };

async function doFetch(path: string, opts: RequestInit = {}, retry = true): Promise<any> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers as Record<string, string> | undefined),
  };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE}/superadmin${path}`, { ...opts, headers });

  if (res.status === 401 && retry && refreshToken) {
    const refreshed = await tryRefresh();
    if (refreshed) return doFetch(path, opts, false);
  }

  let body: any = null;
  const text = await res.text();
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }
  if (!res.ok) {
    const message = (body && body.mensaje) || `Error HTTP ${res.status}`;
    const code = (body && body.error) || "error";
    throw new ApiError(message, res.status, code);
  }
  return body;
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/superadmin/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) { setSuperadminTokens(null, null); return false; }
    const data = await res.json();
    setSuperadminTokens(data.accessToken, data.refreshToken);
    return true;
  } catch {
    setSuperadminTokens(null, null);
    return false;
  }
}

async function doFetchBlob(path: string, retry = true): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
  const res = await fetch(`${API_BASE}/superadmin${path}`, { headers });
  if (res.status === 401 && retry && refreshToken) {
    const refreshed = await tryRefresh();
    if (refreshed) return doFetchBlob(path, false);
  }
  if (!res.ok) {
    const text = await res.text();
    let body: any = null;
    try { body = JSON.parse(text); } catch { /* not JSON */ }
    const message = (body && body.mensaje) || `Error HTTP ${res.status}`;
    const code = (body && body.error) || "error";
    throw new ApiError(message, res.status, code);
  }
  return res.blob();
}

export const superadminApi = {
  get: (path: string) => doFetch(path, { method: "GET" }),
  post: (path: string, body?: any) => doFetch(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: (path: string, body?: any) => doFetch(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  del: (path: string) => doFetch(path, { method: "DELETE" }),
  restoreSession: async (): Promise<boolean> => {
    if (!refreshToken) return false;
    return tryRefresh();
  },
  downloadBlob: (path: string) => doFetchBlob(path),
};

export async function descargarArchivoSuperadmin(path: string, filename: string) {
  const blob = await superadminApi.downloadBlob(path);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
